import React from "react"

const relationLabels = {
  appointments: "Citas",
  odontogramSurfaces: "Superficies de odontograma",
  odontogramEntries: "Odontograma",
  medicalHistories: "Historia médica",
  treatmentLogs: "Tratamientos realizados",
  payments: "Pagos",
  accountReceivables: "Cuentas por cobrar",
  treatmentPlans: "Planes de tratamiento",
  financialAuditEvents: "Eventos financieros",
  clinicalHistories: "Historias clínicas",
  specialtyCases: "Casos clínicos",
  specialtyEvolutions: "Evoluciones clínicas",
  clinicalForms: "Formularios clínicos"
}

function getBlockingRows(preview) {
  const source =
    preview?.blockingCounts && Object.keys(preview.blockingCounts).length
      ? preview.blockingCounts
      : preview?.counts || {}

  return Object.entries(source)
    .filter(([, value]) => Number(value || 0) > 0)
    .map(([key, value]) => ({
      key,
      label: relationLabels[key] || key,
      value: Number(value || 0)
    }))
}

function PatientDeleteModal({
  open,
  mode = "loading",
  patientName = "este paciente",
  preview = null,
  loading = false,
  deleting = false,
  error = "",
  resultMessage = "",
  onClose,
  onConfirmDelete,
  onMerge
}) {
  if (!open) return null

  const rows = getBlockingRows(preview)
  const isBlocked = mode === "blocked"
  const isConfirm = mode === "confirm"
  const isSuccess = mode === "success"
  const isError = mode === "error"
  const isLoading = mode === "loading" || loading

  const title = isSuccess
    ? "Paciente eliminado"
    : isBlocked
      ? "No se puede eliminar este paciente"
      : isError
        ? "No fue posible validar la eliminación"
        : "Eliminar paciente"

  const badgeText = isSuccess
    ? "Completado"
    : isBlocked
      ? "Requiere fusión"
      : isError
        ? "Revisar"
        : "Borrado seguro"

  const badgeStyle = isSuccess
    ? {
        background: "#ecfdf5",
        border: "1px solid #bbf7d0",
        color: "#047857"
      }
    : isBlocked
      ? {
          background: "#fff7ed",
          border: "1px solid #fed7aa",
          color: "#c2410c"
        }
      : isError
        ? {
            background: "#fef2f2",
            border: "1px solid #fecaca",
            color: "#be123c"
          }
        : {
            background: "#fff1f2",
            border: "1px solid #fecaca",
            color: "#be123c"
          }

  return (
    <div style={styles.backdrop} role="dialog" aria-modal="true">
      <div style={styles.modal}>
        <div style={styles.header}>
          <div style={styles.iconWrap}>
            {isSuccess ? "OK" : isBlocked ? "!" : isError ? "!" : "DEL"}
          </div>

          <div style={{ minWidth: 0, flex: 1 }}>
            <div style={{ ...styles.badge, ...badgeStyle }}>{badgeText}</div>
            <h2 style={styles.title}>{title}</h2>
            <p style={styles.subtitle}>
              {isLoading
                ? "Revisando si el paciente tiene información ligada..."
                : isSuccess
                  ? resultMessage || "El paciente fue eliminado correctamente."
                  : isBlocked
                    ? "Este paciente tiene información clínica, financiera o de agenda ligada."
                    : isError
                      ? error || "Ocurrió un problema al revisar este paciente."
                      : `Vas a eliminar definitivamente a ${patientName}.`}
            </p>
          </div>

          <button type="button" onClick={onClose} style={styles.closeButton}>
            ×
          </button>
        </div>

        {isLoading ? (
          <div style={styles.loadingBox}>
            <div style={styles.loader} />
            <div>
              <div style={styles.loadingTitle}>Validando movimientos</div>
              <div style={styles.loadingText}>
                Estamos consultando citas, pagos, odontograma, planes e historia clínica.
              </div>
            </div>
          </div>
        ) : null}

        {isConfirm ? (
          <div style={styles.content}>
            <div style={styles.safeBox}>
              <div style={styles.safeTitle}>Este paciente puede eliminarse</div>
              <div style={styles.safeText}>
                No tiene movimientos clínicos, financieros ni de agenda ligados.
              </div>
            </div>

            <div style={styles.warningBox}>
              <strong>Esta acción no se puede deshacer.</strong>
              <span>
                Si continúas, el paciente saldrá definitivamente de la lista.
              </span>
            </div>
          </div>
        ) : null}

        {isBlocked ? (
          <div style={styles.content}>
            <div style={styles.blockedBox}>
              <strong>No se permite el borrado directo.</strong>
              <span>
                Para evitar pérdida de datos, este paciente debe fusionarse con otro
                paciente o conservarse en el expediente.
              </span>
            </div>

            <div style={styles.sectionTitle}>Movimientos encontrados</div>

            <div style={styles.relationGrid}>
              {rows.length ? (
                rows.map((row) => (
                  <div key={row.key} style={styles.relationItem}>
                    <span>{row.label}</span>
                    <strong>{row.value}</strong>
                  </div>
                ))
              ) : (
                <div style={styles.emptyRelations}>
                  El backend indicó que existe información ligada, pero no devolvió el
                  detalle de movimientos.
                </div>
              )}
            </div>
          </div>
        ) : null}

        {isError ? (
          <div style={styles.content}>
            <div style={styles.errorBox}>
              {error || "No fue posible completar la validación del paciente."}
            </div>
          </div>
        ) : null}

        {isSuccess ? (
          <div style={styles.content}>
            <div style={styles.safeBox}>
              <div style={styles.safeTitle}>Listo</div>
              <div style={styles.safeText}>
                La lista de pacientes fue actualizada.
              </div>
            </div>
          </div>
        ) : null}

        <div style={styles.footer}>
          {isBlocked ? (
            <>
              <button type="button" onClick={onClose} style={styles.secondaryButton}>
                Cerrar
              </button>

              <button
                type="button"
                onClick={onMerge}
                style={{
                  ...styles.primaryButton,
                  background: "#f8fafc",
                  color: "#475569",
                  border: "1px solid #e2e8f0"
                }}
              >
                Fusionar paciente
                <span style={styles.buttonHint}>Elegir destino</span>
              </button>
            </>
          ) : null}

          {isConfirm ? (
            <>
              <button
                type="button"
                onClick={onClose}
                disabled={deleting}
                style={styles.secondaryButton}
              >
                Cancelar
              </button>

              <button
                type="button"
                onClick={onConfirmDelete}
                disabled={deleting}
                style={{
                  ...styles.primaryButton,
                  opacity: deleting ? 0.7 : 1,
                  cursor: deleting ? "wait" : "pointer"
                }}
              >
                {deleting ? "Eliminando..." : "Eliminar definitivamente"}
              </button>
            </>
          ) : null}

          {isLoading || isError || isSuccess ? (
            <button type="button" onClick={onClose} style={styles.secondaryButton}>
              Cerrar
            </button>
          ) : null}
        </div>
      </div>
    </div>
  )
}

const styles = {
  backdrop: {
    position: "fixed",
    inset: 0,
    zIndex: 9999,
    background: "rgba(15, 23, 42, 0.52)",
    backdropFilter: "blur(6px)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: 18
  },
  modal: {
    width: "min(620px, 100%)",
    maxHeight: "min(760px, calc(100vh - 36px))",
    overflow: "auto",
    background: "#ffffff",
    border: "1px solid rgba(226, 232, 240, 0.95)",
    borderRadius: 28,
    boxShadow: "0 28px 90px rgba(15, 23, 42, 0.28)",
    padding: 20
  },
  header: {
    display: "flex",
    gap: 14,
    alignItems: "flex-start"
  },
  iconWrap: {
    width: 48,
    height: 48,
    borderRadius: 18,
    background: "#fff1f2",
    border: "1px solid #fecdd3",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: 22,
    flexShrink: 0
  },
  badge: {
    display: "inline-flex",
    alignItems: "center",
    borderRadius: 999,
    padding: "5px 9px",
    fontSize: 10.5,
    fontWeight: 900,
    letterSpacing: 0.45,
    textTransform: "uppercase",
    marginBottom: 8
  },
  title: {
    margin: 0,
    color: "#0f172a",
    fontSize: 24,
    lineHeight: 1.08,
    letterSpacing: "-0.035em",
    fontWeight: 950
  },
  subtitle: {
    margin: "8px 0 0",
    color: "#475569",
    fontSize: 14,
    lineHeight: 1.5
  },
  closeButton: {
    width: 36,
    height: 36,
    borderRadius: 999,
    border: "1px solid #e2e8f0",
    background: "#ffffff",
    color: "#64748b",
    cursor: "pointer",
    fontSize: 22,
    lineHeight: 1,
    fontWeight: 800,
    flexShrink: 0
  },
  content: {
    marginTop: 18,
    display: "grid",
    gap: 12
  },
  loadingBox: {
    marginTop: 18,
    border: "1px solid #dbeafe",
    background: "#eff6ff",
    borderRadius: 20,
    padding: 14,
    display: "flex",
    gap: 12,
    alignItems: "center"
  },
  loader: {
    width: 18,
    height: 18,
    borderRadius: 999,
    border: "3px solid #bfdbfe",
    borderTopColor: "#2563eb"
  },
  loadingTitle: {
    color: "#1e3a8a",
    fontWeight: 900,
    fontSize: 13
  },
  loadingText: {
    color: "#475569",
    fontSize: 12.5,
    marginTop: 3,
    lineHeight: 1.4
  },
  safeBox: {
    border: "1px solid #bbf7d0",
    background: "#f0fdf4",
    borderRadius: 18,
    padding: 14
  },
  safeTitle: {
    color: "#166534",
    fontWeight: 900,
    fontSize: 14
  },
  safeText: {
    color: "#475569",
    fontSize: 13,
    marginTop: 4,
    lineHeight: 1.45
  },
  warningBox: {
    border: "1px solid #fecaca",
    background: "#fff1f2",
    color: "#7f1d1d",
    borderRadius: 18,
    padding: 14,
    display: "grid",
    gap: 5,
    fontSize: 13,
    lineHeight: 1.45
  },
  blockedBox: {
    border: "1px solid #fed7aa",
    background: "#fff7ed",
    color: "#7c2d12",
    borderRadius: 18,
    padding: 14,
    display: "grid",
    gap: 5,
    fontSize: 13,
    lineHeight: 1.45
  },
  errorBox: {
    border: "1px solid #fecaca",
    background: "#fef2f2",
    color: "#991b1b",
    borderRadius: 18,
    padding: 14,
    fontSize: 13,
    lineHeight: 1.45
  },
  sectionTitle: {
    fontSize: 12,
    color: "#64748b",
    fontWeight: 900,
    textTransform: "uppercase",
    letterSpacing: 0.45
  },
  relationGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
    gap: 10
  },
  relationItem: {
    border: "1px solid #e2e8f0",
    background: "#f8fafc",
    borderRadius: 16,
    padding: "11px 12px",
    display: "flex",
    justifyContent: "space-between",
    gap: 10,
    alignItems: "center",
    color: "#475569",
    fontSize: 13,
    fontWeight: 800
  },
  emptyRelations: {
    gridColumn: "1 / -1",
    border: "1px dashed #cbd5e1",
    background: "#f8fafc",
    borderRadius: 16,
    padding: 13,
    color: "#64748b",
    fontSize: 13,
    lineHeight: 1.45
  },
  footer: {
    marginTop: 18,
    paddingTop: 14,
    borderTop: "1px solid #e2e8f0",
    display: "flex",
    justifyContent: "flex-end",
    gap: 10,
    flexWrap: "wrap"
  },
  secondaryButton: {
    minHeight: 40,
    borderRadius: 14,
    border: "1px solid #e2e8f0",
    background: "#ffffff",
    color: "#334155",
    fontWeight: 900,
    padding: "0 15px",
    cursor: "pointer"
  },
  primaryButton: {
    minHeight: 40,
    borderRadius: 14,
    border: "1px solid #fb7185",
    background: "linear-gradient(135deg, #e11d48, #be123c)",
    color: "#ffffff",
    fontWeight: 950,
    padding: "0 15px",
    cursor: "pointer",
    display: "inline-flex",
    alignItems: "center",
    gap: 8
  },
  buttonHint: {
    fontSize: 10,
    color: "#94a3b8",
    fontWeight: 900
  }
}

export default PatientDeleteModal
